import React, { useState, useEffect } from 'react';
import { FormGroup, Label, Input, Button } from 'reactstrap';
import styled from 'styled-components';

const FormBox = styled.div`
  border: 2px solid #ffc107;
  border-radius: 8px;
  padding: 20px;
  background: linear-gradient(135deg, #fff8e1, #ffffff);
  margin-bottom: 20px;

  h5 {
    color: #ffc107;
    margin-bottom: 15px;
    font-weight: 600;
  }
`;

const HelpText = styled.div`
  background: #fff3cd;
  border-left: 4px solid #ffc107;
  padding: 10px;
  margin-bottom: 15px;
  border-radius: 4px;
  font-size: 13px;
  color: #856404;
`;

const UnderlinePreview = styled.span`
  text-decoration: underline;
  text-decoration-thickness: 2px;
  text-decoration-color: #ffc107;
  font-weight: bold;
  color: #ff6f00;
  padding: 2px 4px;
  background: #fff8e1;
  border-radius: 2px;
`;

const AnswersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

const AnswerItem = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const AnswerBadge = styled.span`
  background: #ffc107;
  color: #000;
  padding: 4px 10px;
  border-radius: 4px;
  font-weight: 600;
  min-width: 30px;
  text-align: center;
  font-size: 12px;
`;

const DeleteButton = styled(Button)`
  padding: 4px 10px !important;
  font-size: 12px !important;
`;

const AddButton = styled(Button)`
  margin-top: 10px;
  font-size: 13px;
`;

interface MakeQuestionFormProps {
  formData: any;
  onChange: (data: any) => void;
}

const MakeQuestionForm: React.FC<MakeQuestionFormProps> = ({ formData, onChange }) => {
  const parseContent = () => {
    try {
      const parsed = typeof formData.content === 'string' && formData.content
        ? JSON.parse(formData.content)
        : null;
      
      if (parsed) {
        let correctQuestions = [''];
        if (parsed.correct_questions && Array.isArray(parsed.correct_questions)) {
          correctQuestions = parsed.correct_questions;
        } else if (parsed.correct_question) {
          correctQuestions = [parsed.correct_question];
        }
        
        return {
          sentence: parsed.sentence || '',
          underlined: parsed.underlined || '',
          correct_questions: correctQuestions,
        };
      }
      
      return {
        sentence: '',
        underlined: '',
        correct_questions: [''],
      };
    } catch {
      return {
        sentence: '',
        underlined: '',
        correct_questions: [''],
      };
    }
  };

  const [contentData, setContentData] = useState(parseContent());

  useEffect(() => {
    onChange({
      ...formData,
      content: JSON.stringify(contentData),
    });
  }, [contentData]);

  const handleFieldChange = (field: string, value: string) => {
    setContentData({
      ...contentData,
      [field]: value,
    });
  };

  const handleQuestionChange = (index: number, value: string) => {
    const newQuestions = [...(contentData.correct_questions || [''])];
    newQuestions[index] = value;
    setContentData({
      ...contentData,
      correct_questions: newQuestions,
    });
  };

  const handleAddQuestion = () => {
    setContentData({
      ...contentData,
      correct_questions: [...(contentData.correct_questions || ['']), ''],
    });
  };

  const handleRemoveQuestion = (index: number) => {
    if ((contentData.correct_questions || ['']).length <= 1) {
      alert('Phải có ít nhất 1 câu hỏi đúng!');
      return;
    }
    const newQuestions = (contentData.correct_questions || ['']).filter((_: any, i: number) => i !== index);
    setContentData({
      ...contentData,
      correct_questions: newQuestions,
    });
  };

  // Hàm render câu với phần gạch chân
  const renderSentenceWithUnderline = () => {
    const { sentence, underlined } = contentData;
    if (!sentence || !underlined) return sentence;

    const parts = sentence.split(underlined);
    if (parts.length === 1) return sentence;

    return (
      <>
        {parts[0]}
        <UnderlinePreview>{underlined}</UnderlinePreview>
        {parts.slice(1).join(underlined)}
      </>
    );
  };

  return (
    <FormBox>
      <h5>❓ Nội dung câu hỏi - Đặt câu hỏi</h5>
      
      <HelpText>
        💡 <strong>Hướng dẫn:</strong> Cho một câu với phần được gạch chân, học sinh đặt câu hỏi phù hợp.
        <br/>
        Ví dụ: "She bought <u>a bike</u>." → Câu hỏi: "What did she buy?"
      </HelpText>

      <FormGroup>
        <Label>Câu đầy đủ: *</Label>
        <Input
          type="textarea"
          value={contentData.sentence || ''}
          onChange={(e) => handleFieldChange('sentence', e.target.value)}
          placeholder="Nhập câu đầy đủ...

Ví dụ: Tom goes to school by bike."
          rows={3}
        />
      </FormGroup>

      <FormGroup>
        <Label>Phần được gạch chân (cần hỏi): *</Label>
        <Input
          type="text"
          value={contentData.underlined || ''}
          onChange={(e) => handleFieldChange('underlined', e.target.value)}
          placeholder="Nhập phần cần gạch chân...

Ví dụ: by bike"
        />
        <small className="form-text text-muted">
          💡 Nhập chính xác từ/cụm từ xuất hiện trong câu ở trên
        </small>
      </FormGroup>

      <FormGroup>
        <Label>Các câu hỏi đúng (đáp án): *</Label>
        <AnswersList>
          {(contentData.correct_questions || ['']).map((question: string, index: number) => (
            <AnswerItem key={index}>
              <AnswerBadge>{index + 1}</AnswerBadge>
              <Input
                type="textarea"
                value={question}
                onChange={(e) => handleQuestionChange(index, e.target.value)}
                placeholder={`Câu hỏi ${index + 1}...`}
                rows={2}
                style={{ flex: 1 }}
              />
              <DeleteButton
                color="danger"
                size="sm"
                onClick={() => handleRemoveQuestion(index)}
                disabled={(contentData.correct_questions || ['']).length <= 1}
              >
                ✕
              </DeleteButton>
            </AnswerItem>
          ))}
        </AnswersList>
        <AddButton
          color="success"
          size="sm"
          onClick={handleAddQuestion}
        >
          + Thêm câu hỏi
        </AddButton>
        <small className="form-text text-muted" style={{ marginTop: '10px' }}>
          💡 Có thể có nhiều cách hỏi đúng khác nhau
        </small>
      </FormGroup>

      {/* Preview */}
      <div style={{ 
        background: '#fff8e1', 
        padding: '15px', 
        borderRadius: '4px',
        marginTop: '15px'
      }}>
        <strong style={{ fontSize: '13px', color: '#f57c00' }}>📋 Xem trước:</strong>
        {contentData.sentence && (
          <div style={{ 
            marginTop: '10px',
            padding: '12px',
            background: 'white',
            borderRadius: '4px',
            marginBottom: '12px',
            fontSize: '15px',
            lineHeight: '1.6'
          }}>
            <div style={{ marginBottom: '8px' }}>
              <strong style={{ color: '#666', fontSize: '13px' }}>
                Đặt câu hỏi cho phần gạch chân:
              </strong>
            </div>
            {renderSentenceWithUnderline()}
          </div>
        )}
        {(contentData.correct_questions || ['']).filter((q: string) => q.trim()).length > 0 && (
          <div style={{ 
            background: '#d4edda',
            padding: '12px',
            borderRadius: '4px',
            border: '1px solid #28a745'
          }}>
            <strong style={{ color: '#155724', fontSize: '13px' }}>✓ Các câu hỏi đúng:</strong>
            <ul style={{ margin: '5px 0 0 20px', paddingLeft: 0 }}>
              {(contentData.correct_questions || ['']).filter((q: string) => q.trim()).map((question: string, idx: number) => (
                <li key={idx} style={{ color: '#155724', fontSize: '14px' }}>{question}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Examples */}
      <div style={{ 
        background: '#e3f2fd', 
        padding: '12px', 
        borderRadius: '4px',
        marginTop: '15px',
        fontSize: '13px'
      }}>
        <strong style={{ color: '#1976d2' }}>📖 Ví dụ:</strong>
        <div style={{ marginTop: '8px', color: '#555' }}>
          <div style={{ marginBottom: '8px' }}>
            • Câu: "Tom goes to school <u>by bike</u>."
            <br/>
            → Câu hỏi: "How does Tom go to school?"
          </div>
          <div style={{ marginBottom: '8px' }}>
            • Câu: "She bought <u>a new dress</u>."
            <br/>
            → Câu hỏi: "What did she buy?"
          </div>
          <div>
            • Câu: "They are going <u>to the cinema</u>."
            <br/>
            → Câu hỏi: "Where are they going?"
          </div>
        </div>
      </div>

      {/* Debug JSON */}
      <details style={{ marginTop: '15px' }}>
        <summary style={{ cursor: 'pointer', color: '#666', fontSize: '14px' }}>
          🔍 Xem JSON (Debug)
        </summary>
        <pre style={{ 
          background: '#f5f5f5', 
          padding: '10px', 
          borderRadius: '4px',
          fontSize: '12px',
          maxHeight: '200px',
          overflow: 'auto',
          marginTop: '10px'
        }}>
          {JSON.stringify(contentData, null, 2)}
        </pre>
      </details>
    </FormBox>
  );
};

export default MakeQuestionForm;
